/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import { Grid, Icon, Divider } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// import Header from "layouts/pages/profile/components/Header"; Commenting for Issue: #53
import CourseCard from "examples/Cards/CourseCard";

// Services
import AuthService from "services/auth-service";
import CrudService from "services/cruds-service";

import { analytics } from "../../dashboards/analytics/analytics";
import { events } from "../../dashboards/analytics/events";
import { AuthContext } from "context";


function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);           // cancel on next keystroke
  }, [value, delay]);
  return debounced;
}


function CourseCatalog() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState(availableCourses);
  const [showFreeCourses, setShowFreeCourses] = useState(false);
  const [showPurchased, setShowPurchased] = useState(false);

  const { getCurrentUser } = useContext(AuthContext);

  useEffect(() => {
    const start_time = Date.now();
    const getUserId = async () => {
      const user = await getCurrentUser();
      return user;
    };
    getUserId().then((user) => {
      analytics.track(events.VIEW_PAGE, { page: "course-catalog", userId: user });
    });
    return () => {
      getUserId().then((user) => {
        analytics.track(events.PAGE_LEFT, { page: "course-catalog", userId: user, start_time: start_time, end_time: Date.now() });
      });
    }
  }, []);


  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      analytics.track(events.SEARCH_FILTER_APPLIED, {
        query: debouncedSearch,
        results: filteredCourses.length,
      });
    }
  }, [debouncedSearch]);


  const [user, setUser] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    newPassword: "",
    currentPassword: "",
    confirmPassword: "",
    profile_image: "",
  });

  const handleShowFreeCoursesSwitch = (event) => {
    setShowFreeCourses(event.target.checked);
    if (event.target.checked) {
      analytics.track(events.FREE_COURSE_FILTER_APPLIED, { userId: user.id, showFreeCourses: event.target.checked });
    }
  };

  const handlePurchasedSwitchChange = (event) => {
    setShowPurchased(event.target.checked);
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await AuthService.getProfile();
        setUser((prevUser) => ({
          ...prevUser,
          id: response.data.id,
          name: response.data.attributes.name,
          email: response.data.attributes.email,
          profile_image: response.data.attributes.profile_image,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        let courses = await CrudService.getCoursesSummaries();
        console.log("Courses fetched:", courses);


        const purchasedCourses = (await CrudService.getAvailableCourses(
          response.data.id
        )) ?? [];

        console.log("Purchased courses fetched:", purchasedCourses);

        courses = courses.map(course => ({
          ...course,
          purchased: purchasedCourses.includes(course._id)
        }));

        console.log("Courses after marking purchased:", courses);
        //Sort Courses.data in ascending order wrt time creation
        courses.sort((a, b) => {
          return new Date(b.creationDate) - new Date(a.creationDate);
        });
        setAvailableCourses(courses);
        setFilteredCourses(courses);
      } catch (error) {
        alert(
          "Error fetching courses. Please refresh the page or try again later."
        );
        navigate("/course-catalog");
        console.log(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    setSearch(search);
    // Create a shallow copy of the available courses
    const coursesCopy = [...availableCourses];
    let searchLowerCase = search.toLowerCase();

    const filteredCourses = coursesCopy.filter((course) => {
      const matchessearch =
        course.app_name.toLowerCase().includes(searchLowerCase) ||
        course.short_description
          .toLowerCase()
          .includes(searchLowerCase);

      const isPurchasedCourse = showPurchased
        ? course.purchased
        : true;
      const isFreeCourse = showFreeCourses
        ? !course.external_link
        : true;

      // Only include the course if it matches both the search text and the active filters
      return matchessearch && isPurchasedCourse && isFreeCourse;
    });

    if (search === "" && filteredCourses.length === 0) {
      setSearch(search);
      setFilteredCourses(availableCourses);
      return;
    }

    setFilteredCourses(filteredCourses);
  }, [search, showPurchased, showFreeCourses]);

  // Function to explore a course
  const exploreCard = (course_id) => {
    navigate("/course-content/" + course_id);
  };

  return (
    <DashboardLayout id={0}>
      <DashboardNavbar />
      {/* <Header user={user} /> */}
      <MDTypography variant="h3" mt={2}>
        Course Catalog
      </MDTypography>
      <Divider />
      {/* Search functionality */}
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={10}>
            <MDInput
              placeholder="Search..."
              value={search}
              size="small"
              fullWidth
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <MDButton variant="outlined" fullWidth color="info">
              <Icon>search</Icon>&nbsp; Search
            </MDButton>
          </Grid>

          <Grid item xs={12} display="flex">
            <FormControlLabel
              control={
                <Switch
                  checked={showPurchased}
                  onChange={handlePurchasedSwitchChange}
                />
              }
              label="Purchased"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showFreeCourses}
                  onChange={handleShowFreeCoursesSwitch}
                />
              }
              label="Free"
            />
          </Grid>
        </Grid>
      </MDBox>

      {/* Display the courses */}
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          {(filteredCourses.length == 0 || availableCourses.length === 0) && (
            <MDTypography variant="h5" color="textSecondary" ml={3} mt={2}>
              No courses found
            </MDTypography>
          )}
          {filteredCourses.map((course) => (
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mt={3}>
                <CourseCard
                  image={course.app_image_location}
                  title={course.app_name}
                  description={course.short_description}
                  label="Explore"
                  action={<></>}
                  exploreCard={() => exploreCard(course._id)}
                  moduleCount={course.num_modules}
                  height="auto"
                  purchased={course.purchased}
                  completed={course.completed}
                  externalLink={course.external_link}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CourseCatalog;